import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./MemosAndNotes.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import Editor from "../../../components/Editor";
import Dropdown from "../../../components/Dropdown";
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { getProspectMemos, createProspectMemo } from "../../../services/prospectMemoStudent.service";
import { updateProspect } from "../../../services/prospect.service";
import { showSuccess, showError } from "../../../services/toast.service";
import { useAuth } from "../../../contexts/AuthContext";

const ACTION_OPTIONS = ['Prise de contact', 'Prise de rendez-vous', 'Relance', 'Autre'];
const CANAL_OPTIONS = ['Appel téléphonique', 'Email', 'Visio', 'SMS', 'Autre'];
const STATUS_OPTIONS = ['Nouveau', 'En cours', 'Hors cible', 'Success'];

const MemosAndNotes = ({ className, prospect, onStatusUpdate }) => {
  const prospectId = prospect?.id;
  const { currentUser } = useAuth();
  const [content, setContent] = useState(() => EditorState.createEmpty());
  const [topic, setTopic] = useState('');
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedCanal, setSelectedCanal] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(prospect?.status || 'Nouveau');

  const handleDropdownChange = (field, value) => {
    if (field === 'action') {
      setSelectedAction(value);
    } else if (field === 'canal') {
      setSelectedCanal(value);
    } else if (field === 'status') {
      setSelectedStatus(value);
    }
  };

  const fetchMemos = useCallback(async () => {
    if (!prospectId) return;

    setLoading(true);
    try {
      const prospectMemos = await getProspectMemos(prospectId);
      setMemos(prospectMemos || []);
    } catch (error) {
      console.error("Error fetching memos:", error);
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => {
    fetchMemos();
    if (prospect?.status) {
      setSelectedStatus(prospect.status);
    }
  }, [prospect, fetchMemos]);

  const handleSubmit = async () => {
    if (!topic.trim()) {
      showError("Veuillez saisir un sujet pour le mémo");
      return;
    }
    
    const contentText = content.getCurrentContent().getPlainText();
    if (!contentText.trim()) {
      showError("Veuillez ajouter du contenu au mémo");
      return;
    }
    
    setSubmitting(true);
    try {
      const rawContent = convertToRaw(content.getCurrentContent());
      const memoData = {
        prospect_id: prospectId,
        topic: topic,
        action: selectedAction.toLowerCase().replace(/ /g, '_'),
        channel: selectedCanal.toLowerCase().replace(/ /g, '_'),
        content: JSON.stringify(rawContent),
        created_by: currentUser?.id
      };

      const newMemo = await createProspectMemo(memoData);
      setMemos(prevMemos => [newMemo, ...prevMemos]);
      
      try {
        const updateData = { last_contact_at: new Date().toISOString() };
        if (selectedStatus !== prospect.status) {
          updateData.status = selectedStatus;
        }
        await updateProspect(prospectId, updateData);
        onStatusUpdate?.();
      } catch (error) {
        console.error("Erreur lors de la mise à jour du prospect:", error);
        showError("Impossible de mettre à jour le prospect");
      }
      
      setTopic('');
      setSelectedAction('');
      setSelectedCanal('');
      setContent(EditorState.createEmpty());
      
      showSuccess("Mémo créé avec succès");
    } catch (error) {
      console.error("Erreur:", error);
      showError("Impossible de créer le mémo");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const renderMemoContent = (content) => {
    try {
      const contentObj = JSON.parse(content);
      if (contentObj.blocks) {
        return contentObj.blocks.map((block, index) => (
          <p key={index}>{block.text}</p>
        ));
      }
      return <p>{content}</p>;
    } catch (e) {
      return <p>{content}</p>;
    }
  };

  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title="Mémos"
        classTitle="title-red"
      >
        <div className={styles.description}>
          <div className={styles.memosList}>
            <h3 className={styles.memosTitle}>Mémos du prospect</h3>
            {loading ? (
              <div className={styles.loading}>
                <div className="spinner-large" style={{ margin: '20px auto' }} />
                <div>Chargement des mémos...</div>
              </div>
            ) : memos.length === 0 ? (
              <div className={styles.empty}>Aucun mémo pour ce prospect</div>
            ) : (
              memos.map((memo) => (
                <div key={memo.id} className={styles.memoItem}>
                  <div className={styles.memoHeader}>
                    <div className={styles.memoDate}>
                      {formatDate(memo.created)}
                    </div>
                    {memo.expand?.created_by && (
                      <div className={styles.memoAuthor}>
                        par {memo.expand.created_by.first_name} {memo.expand.created_by.last_name}
                      </div>
                    )}
                  </div>
                  <div className={styles.login}>{memo.topic}</div>
                  <div className={styles.memoContent}>
                    {renderMemoContent(memo.content)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>
      <Card
        className={cn(styles.card, className)}
        title="Nouveau mémo"
        classTitle="title-green"
      >
        <div className={styles.description}>
          <div className={styles.group}>
            <div className={styles.field}>
              <Dropdown
                className={styles.dropdown}
                label="Action"
                value={selectedAction}
                setValue={(value) => handleDropdownChange('action', value)}
                options={ACTION_OPTIONS}
              />
            </div>
            <div className={styles.field}>
              <Dropdown
                className={styles.dropdown}
                label="Canal"
                value={selectedCanal}
                setValue={(value) => handleDropdownChange('canal', value)}
                options={CANAL_OPTIONS}
              />
            </div>
            <div className={styles.field}>
              <Dropdown
                className={styles.dropdown}
                label="Statut du prospect"
                value={selectedStatus}
                setValue={(value) => handleDropdownChange('status', value)}
                options={STATUS_OPTIONS}
              />
            </div>
          </div>
          <TextInput
            className={styles.field}
            label="Sujet"
            name="topic"
            type="text"
            placeholder="Sujet du mémo"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
          />
          <Editor
            state={content}
            onChange={setContent}
            classEditor={styles.editor}
            label="Contenu"
            placeholder="Ajoutez le contenu du mémo ici..."
          />
          <div className={styles.actions}>
            <button
              className="button-green"
              onClick={handleSubmit}
              disabled={submitting}
            >
              <span>{submitting ? 'Création en cours...' : 'Créer le mémo'}</span>
              {submitting && <div className="spinner-small" />}
            </button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default MemosAndNotes;